import { useMemo } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router'

import type { ViewContextInput } from '../../graphql/generated'
import { useCase, useTitle } from '../hooks'

/**
 * Baut den UI-Kontext, den der KI-Chat beim Senden einer Nachricht mitschickt
 * (aktuelle Route/Fall/Vorgang/Tab/Titel) — siehe ViewContextInput im
 * GraphQL-Schema. Es werden bewusst nur tatsächlich vorhandene Werte
 * gesetzt, keine leeren Strings/undefined-Platzhalter.
 */
export const useCurrentViewContext = (): ViewContextInput => {
  const location = useLocation()
  const params = useParams<{ id?: string; processingId?: string }>()
  const [searchParams] = useSearchParams()
  const { id: caseContextId, processingId: caseContextProcessingId } = useCase()
  const { values: titleValues } = useTitle()

  const searchParamsKey = searchParams.toString()

  return useMemo(() => {
    const context: ViewContextInput = { route: location.pathname }

    const caseId = params.id || caseContextId
    if (caseId) context.caseId = caseId

    const processingId = params.processingId || caseContextProcessingId
    if (processingId) context.processingId = processingId

    const tab = searchParams.get('tab')
    if (tab) context.tab = tab

    const subTab = searchParams.get('subTab')
    if (subTab) context.subTab = subTab

    const action = searchParams.get('action')
    if (action) context.action = action

    // titleValues.title kann auch ein ReactElement sein (siehe TitleContext) —
    // in dem Fall gibt es keinen sinnvollen String-Titel für den KI-Kontext.
    if (typeof titleValues?.title === 'string' && titleValues.title) {
      context.title = titleValues.title
    }

    return context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    params.id,
    params.processingId,
    caseContextId,
    caseContextProcessingId,
    searchParamsKey,
    titleValues?.title
  ])
}
